import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import image1 from '../images/1.jpg';
import obyvackaKrb from '../images/obyvackakrb.png';
import barvinokVonka2 from '../images/barvinokvonka2.png';
import vonkuHore from '../images/vonkuhore.png';
import vonkuVonku from '../images/vonkuvonku.png';
import vonzBoku from '../images/vonzboku.png';
import LazyImage from '../components/LazyImage';
import hala from '../images/hala.png';

const Gallery = () => {
  const { t } = useLanguage();

  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [image1Ref, image1Visible] = useScrollAnimation();
  const [image2Ref, image2Visible] = useScrollAnimation();
  const [image3Ref, image3Visible] = useScrollAnimation();
  const [image4Ref, image4Visible] = useScrollAnimation();
  const [image5Ref, image5Visible] = useScrollAnimation();
  const [image6Ref, image6Visible] = useScrollAnimation();

  return (
    <>
      <Helmet>
        <title>Galéria – Chata Barvinok</title>
        <meta
          name="description"
          content="Fotogaléria chaty Barvinok v Litmanovej – interiér, exteriér a okolie pri lyžiarskom stredisku."
        />
      </Helmet>
      <div className="min-h-screen bg-white dark:bg-black pt-24">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            ref={headerRef}
            className={`text-center mb-16 transition-all duration-800 ${headerVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
          >
            <span className="text-copper-400 text-sm font-semibold uppercase tracking-widest mb-4 block">
              {t('home.gallery')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {t('home.galleryTitle')}
            </h2>
            <div className="w-24 h-1 bg-copper-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('home.gallerySubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div 
              ref={image1Ref}
              className={`group relative overflow-hidden rounded-lg shadow-2xl transition-all duration-800 ${image1Visible ? 'animate-fade-in-left opacity-100' : 'opacity-0'}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <LazyImage 
                  src={hala} 
                  alt={t('home.interior')} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{t('home.interior')}</h3>
                  <p className="text-gray-300">{t('home.interiorDesc')}</p>
                </div>
              </div>
            </div>

            <div 
              ref={image2Ref}
              className={`group relative overflow-hidden rounded-lg shadow-2xl transition-all duration-800 delay-100 ${image2Visible ? 'animate-fade-in-right opacity-100' : 'opacity-0'}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <LazyImage 
                  src={obyvackaKrb} 
                  alt={t('home.interior')} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{t('home.interior')}</h3>
                  <p className="text-gray-300">{t('home.interiorDesc')}</p>
                </div>
              </div>
            </div>

            <div 
              ref={image3Ref}
              className={`group relative overflow-hidden rounded-lg shadow-2xl transition-all duration-800 delay-200 ${image3Visible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <LazyImage 
                  src={vonkuHore} 
                  alt={t('home.exterior')} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{t('home.exterior')}</h3>
                  <p className="text-gray-300">{t('home.exteriorDesc')}</p>
                </div>
              </div>
            </div>

            <div 
              ref={image4Ref}
              className={`group relative overflow-hidden rounded-lg shadow-2xl transition-all duration-800 delay-300 ${image4Visible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <LazyImage 
                  src={vonkuVonku} 
                  alt={t('home.exterior')} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{t('home.exterior')}</h3>
                  <p className="text-gray-300">{t('home.exteriorDesc')}</p>
                </div>
              </div>
            </div>

            <div 
              ref={image5Ref}
              className={`group relative overflow-hidden rounded-lg shadow-2xl transition-all duration-800 delay-400 ${image5Visible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <LazyImage 
                  src={vonzBoku} 
                  alt={t('home.exterior')} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{t('home.exterior')}</h3>
                  <p className="text-gray-300">{t('home.exteriorDesc')}</p>
                </div>
              </div>
            </div>

            <div 
              ref={image6Ref}
              className={`group relative overflow-hidden rounded-lg shadow-2xl transition-all duration-800 delay-500 ${image6Visible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <LazyImage 
                  src={barvinokVonka2} 
                  alt={t('home.exterior')} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{t('home.exterior')}</h3>
                  <p className="text-gray-300">{t('home.exteriorDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Gallery;

